/**
 * DIEUKYDIEU.TV - SEARCH DATABASE & HISTORY PERSISTENCE
 */
import { t } from '../i18n.js';
import { getSectionsData } from '../explore.js';
import { removeVietnameseTones } from './search-text.js';
import { videoList, musicList, audioMediaList } from '../media-data.js';

export const SEARCH_HISTORY_KEY = 'dkd_search_history';

export const DEFAULT_SEARCH_HISTORY = [
    "Khỏi bệnh thần kỳ",
    "Thiền định & Sức khỏe",
    "Khoa học tâm linh",
    "Âm nhạc chữa lành",
    "Chân tướng sự thật"
];

export const POPULAR_SEARCH_TOPICS = [
    { label: "Thiền định & Sức khỏe", icon: "self_improvement" },
    { label: "Khỏi bệnh thần kỳ", icon: "auto_awesome" },
    { label: "Khoa học tâm linh", icon: "psychology" },
    { label: "Âm nhạc chữa lành", icon: "headphones" },
    { label: "Chân tướng sự thật", icon: "verified" },
    { label: "Văn hóa truyền thống", icon: "temple_buddhist" },
    { label: "Luân hồi chuyển sinh", icon: "all_inclusive" },
    { label: "Chuyển biến tâm tính", icon: "favorite" }
];

export function getFeaturedSuggestions() {
    const sitemapData = getSectionsData();
    const suggestions = [];
    const addedTitles = new Set();

    if (Array.isArray(sitemapData) && sitemapData.length > 0) {
        sitemapData.forEach((sec, sIdx) => {
            const secName = sec.name || '';
            const secSlug = sec.slug || '';
            const articles = sec.articles || [];

            articles.slice(0, 1).forEach((art, aIdx) => {
                const title = art.title || '';
                const titleLower = title.toLowerCase().trim();
                if (!titleLower || addedTitles.has(titleLower)) return;
                addedTitles.add(titleLower);

                let type = 'article';
                const tagLower = (art.tag || '').toLowerCase();
                if (tagLower.includes('video') || titleLower.startsWith('video') || secSlug.includes('video')) {
                    type = 'video';
                } else if (tagLower.includes('audio') || tagLower.includes('nhạc') || secSlug.includes('nhac') || secSlug.includes('audio')) {
                    type = 'audio';
                }

                suggestions.push({
                    id: art.id || `feat-${sIdx}-${aIdx}`,
                    title: title,
                    desc: art.desc || '',
                    tag: art.tag || secName,
                    type: type,
                    author: art.author || 'dieukydieu.tv',
                    duration: art.duration || (type === 'video' ? '12:30 xem' : type === 'audio' ? '25:00 nghe' : ''),
                    img: art.img || 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=600&q=80'
                });
            });
        });
    }

    return suggestions.slice(0, 5);
}

export const FEATURED_SUGGESTIONS = getFeaturedSuggestions();

export const MOCK_AUTHORS = [
    "dieukydieu.tv",
    "Ban Biên Tập DKD",
    "Học giả Minh Huệ",
    "Phóng viên Quốc tế"
];

let _unifiedSearchDB = null;
let _cachedSitemapCount = -1;

function attachSearchIndexFields(item) {
    const combined = `${item.title || ''} ${item.desc || ''} ${item.author || ''} ${item.tag || ''}`;
    item._searchRaw = combined.toLowerCase();
    item._searchNorm = removeVietnameseTones(combined);
    return item;
}

export function getUnifiedSearchDatabase() {
    const sitemapData = getSectionsData();
    const currentCount = Array.isArray(sitemapData) ? sitemapData.length : 0;
    if (_unifiedSearchDB && _cachedSitemapCount === currentCount) {
        return _unifiedSearchDB;
    }

    const list = [];
    const addedTitles = new Set();

    if (Array.isArray(sitemapData)) {
        sitemapData.forEach((sec, sIdx) => {
            const secName = sec.name || '';
            const secSlug = sec.slug || '';
            const articles = sec.articles || [];

            articles.forEach((art, aIdx) => {
                const title = art.title || '';
                const titleLower = title.toLowerCase().trim();
                if (!titleLower || addedTitles.has(titleLower)) return;
                addedTitles.add(titleLower);

                let type = 'article';
                const tagLower = (art.tag || '').toLowerCase();
                if (tagLower.includes('video') || titleLower.startsWith('video') || secSlug.includes('video')) {
                    type = 'video';
                } else if (tagLower.includes('audio') || tagLower.includes('nhạc') || secSlug.includes('nhac') || secSlug.includes('audio')) {
                    type = 'audio';
                }

                const author = art.author || 'dieukydieu.tv';

                list.push(attachSearchIndexFields({
                    id: art.id || `art-${sIdx}-${aIdx}`,
                    title: title,
                    desc: art.desc || '',
                    tag: art.tag || secName,
                    type: type,
                    author: author,
                    duration: art.duration || (type === 'video' ? '12:30 xem' : type === 'audio' ? '25:00 nghe' : ''),
                    img: art.img || 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=600&q=80'
                }));
            });
        });
    }

    // Index Video Library items
    if (Array.isArray(videoList)) {
        videoList.forEach((vid, vIdx) => {
            const title = vid.title || '';
            const titleLower = title.toLowerCase().trim();
            if (!titleLower || addedTitles.has(titleLower)) return;
            addedTitles.add(titleLower);
            list.push(attachSearchIndexFields({
                id: vid.id || `vid-${vIdx}`,
                title,
                desc: vid.desc || '',
                tag: vid.cat || 'Video',
                type: 'video',
                author: 'dieukydieu.tv',
                duration: vid.duration ? `${vid.duration} xem` : '12:30 xem',
                img: vid.thumb || 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=600&q=80'
            }));
        });
    }

    // Index Music & Audio Library items
    const allAudioItems = [...(Array.isArray(musicList) ? musicList : []), ...(Array.isArray(audioMediaList) ? audioMediaList : [])];
    allAudioItems.forEach((aud, aIdx) => {
        const title = aud.title || '';
        const titleLower = title.toLowerCase().trim();
        if (!titleLower || addedTitles.has(titleLower)) return;
        addedTitles.add(titleLower);
        list.push(attachSearchIndexFields({
            id: aud.id || `aud-${aIdx}`,
            title,
            desc: aud.desc || aud.artist || '',
            tag: aud.cat || 'Audio',
            type: 'audio',
            author: aud.artist || 'dieukydieu.tv',
            duration: aud.duration ? `${aud.duration} nghe` : '25:00 nghe',
            img: aud.art || aud.thumb || 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=600&q=80'
        }));
    });

    _cachedSitemapCount = currentCount;
    _unifiedSearchDB = list;
    return _unifiedSearchDB;
}

export function getSearchHistory() {
    try {
        const raw = localStorage.getItem(SEARCH_HISTORY_KEY);
        if (raw !== null) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
        }
    } catch (e) {
        console.warn('Cannot read search history from localStorage', e);
    }
    return [...DEFAULT_SEARCH_HISTORY];
}

export function saveSearchHistory(list) {
    try {
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(list.slice(0, 10)));
    } catch (e) {
        console.warn('Cannot save search history', e);
    }
}

export function addSearchHistory(term) {
    if (!term || !term.trim()) return;
    const clean = term.trim();
    let history = getSearchHistory().filter(h => h.toLowerCase() !== clean.toLowerCase());
    history.unshift(clean);
    saveSearchHistory(history);
}

export function removeSearchHistory(termOrEl, e) {
    if (e) {
        e.stopPropagation();
        e.preventDefault();
    }
    let term = '';
    if (termOrEl && typeof termOrEl === 'object' && termOrEl.dataset) {
        term = termOrEl.dataset.term || '';
    } else {
        term = termOrEl || '';
    }

    let history = getSearchHistory().filter(h => h !== term);
    saveSearchHistory(history);

    const desktopResults = document.getElementById('desktop-search-results');
    const desktopInput = document.getElementById('desktop-search-input');
    if (desktopResults && desktopResults.classList.contains('show') && (!desktopInput || !desktopInput.value.trim())) {
        if (typeof renderDesktopEmptyState === 'function') renderDesktopEmptyState(desktopResults); else if (typeof window.renderDesktopEmptyState === 'function') window.renderDesktopEmptyState(desktopResults);
    }

    const mobileInput = document.getElementById('mobile-search-input');
    if (!mobileInput || !mobileInput.value.trim()) {
        if (typeof renderMobileEmptyState === 'function') renderMobileEmptyState(); else if (typeof window.renderMobileEmptyState === 'function') window.renderMobileEmptyState();
    }
}

export function clearAllSearchHistory(e) {
    if (e) {
        e.stopPropagation();
        e.preventDefault();
    }
    saveSearchHistory([]);

    const desktopResults = document.getElementById('desktop-search-results');
    const desktopInput = document.getElementById('desktop-search-input');
    if (desktopResults && desktopResults.classList.contains('show') && (!desktopInput || !desktopInput.value.trim())) {
        renderDesktopEmptyState(desktopResults);
    }

    const mobileInput = document.getElementById('mobile-search-input');
    if (!mobileInput || !mobileInput.value.trim()) {
        renderMobileEmptyState();
    }
}

